import { z } from 'zod';
import { ColorSetIdSchema, ThemeIdSchema } from '../catalog/platformCatalogIds';
import { LayoutComponentTypeSchema } from '../content/studio';
import { SPACING_ENUM_KEY_VALUES } from '../theme/types';

export const AI_JOB_TYPES = ['text.field', 'image.asset'] as const;
export type AiJobType = (typeof AI_JOB_TYPES)[number];

export const AI_JOB_STATUSES = [
  'queued',
  'running',
  'succeeded',
  'failed',
  'committed',
  'discarded',
] as const;
export type AiJobStatus = (typeof AI_JOB_STATUSES)[number];

const editTargetSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('content-field'),
    fieldKey: z.string().min(1),
  }),
  z.object({
    kind: z.literal('media-field'),
    fieldKey: z.string().min(1),
  }),
]);

export const AiJobTargetSchema = z.object({
  blockId: z.string().uuid(),
  componentType: LayoutComponentTypeSchema,
  editTarget: editTargetSchema,
  locale: z.string().min(2).max(16),
  /**
   * When `editTarget.fieldKey` is `openGraphImage`, commit writes page SEO
   * (custom OG) instead of block media.
   */
  pageId: z.string().uuid().optional(),
});

export type AiJobTarget = z.infer<typeof AiJobTargetSchema>;

export const AiJobCreateSchema = z.object({
  type: z.enum(AI_JOB_TYPES),
  brief: z.string().min(1).max(2000),
  target: AiJobTargetSchema,
  variantCount: z.union([z.literal(1), z.literal(2)]).optional().default(1),
});

export type AiJobCreate = z.infer<typeof AiJobCreateSchema>;

export const AiJobCommitSchema = z.object({
  variantId: z.string().uuid(),
});

export const BrandContextPackSchema = z.object({
  version: z.literal(1),
  projectId: z.string().uuid(),
  locale: z.string(),
  business: z.object({
    name: z.string(),
    vertical: z.string(),
    packageTier: z.string(),
  }),
  theme: z.object({
    themeId: ThemeIdSchema,
    colorsetId: ColorSetIdSchema,
    spacingEnum: z.enum(SPACING_ENUM_KEY_VALUES),
  }),
  branding: z
    .object({
      legalName: z.string().optional(),
      description: z.string().optional(),
    })
    .optional(),
  layout: z.object({
    blockId: z.string().uuid(),
    componentType: LayoutComponentTypeSchema,
    fieldKey: z.string(),
    fieldKind: z.enum(['content-field', 'media-field']),
    currentValue: z.string(),
    siblingCopy: z.record(z.string(), z.string()),
    aspectRatio: z.string().optional(),
    slot: z.string().optional(),
    intent: z.string().optional(),
    maxChars: z.number().int().positive().optional(),
  }),
  paletteHints: z.array(z.string()).optional(),
});

export type BrandContextPack = z.infer<typeof BrandContextPackSchema>;

export const GenerationPlanSchema = z.object({
  prompt: z.string(),
  negativePrompt: z.string().optional(),
  styleTags: z.array(z.string()).optional(),
  aspectRatio: z.string().optional(),
});

export type GenerationPlan = z.infer<typeof GenerationPlanSchema>;

export const AiTextVariantPayloadSchema = z.object({
  kind: z.literal('text'),
  text: z.string(),
});

export const AiImageVariantPayloadSchema = z.object({
  kind: z.literal('image'),
  url: z.string().url(),
});

export const AiTextGenerationResultSchema = z.object({
  variants: z.array(z.object({ text: z.string().min(1) })).min(1).max(2),
});
